import React, { useRef } from 'react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createProduct, getProducts, deleteProduct,
  updateProduct, getSingleProduct, activateProduct
} from '../../redux/features/product/productSlice';
import { getallCategories } from '../../redux/features/product/categorySlice';
import { toast } from 'react-toastify';
import { useTable, usePagination, useGlobalFilter, useFilters, useSortBy } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser"
import { MdEditSquare, MdDeleteForever } from 'react-icons/md';
import { VscLayersActive } from 'react-icons/vsc';
import { AiOutlinePlus } from "react-icons/ai";
import ButtonLoading from '../../loader/ButtonLoader';
import ReactToPrint from 'react-to-print';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { Modal } from "bootstrap";
import Select from "react-select";
import { ColumnToggle } from './ColumnToggle';
import { getBoxInfo } from '../../utils/boxInfo';
import { getPresetFields, hasPreset } from '../../utils/productPresets';
import { calcBuyOptions } from '../../utils/ceramicCalculator';
import { EthiopianDateCell } from './EthiopianDateCell';



const initialState = {
  name:            "",
  sku:             "",
  category:        "",
  sellingPrice:   "",
  minSellingPrice: "",
  maxSellingPrice: "",
  totalQuantity: "",
  description:     "",
  attributes:      [],
  sizePerItem: "",   // m²/pc or ml/pc
  itemsPerBox: "",   // pieces per carton
  sizePerBox:  "",   // m²/box or ml/box
  itemWidth:   "",   // optional — for m² auto-calc from W×L
  itemLength:  "",   // optional
}

export const Products = () => {
  useRedirectLogOutUser();

  const dispatch     = useDispatch();
  const componentRef = useRef();

  const addModalRef    = useRef(null);
  const editModalRef   = useRef(null);
  const deleteModalRef = useRef(null);
  const activateModalRef = useRef(null);

  const [productForm, setProductForm] = useState(initialState);
  const [isLoading, setIsLoading]     = useState(false);
  const [printView, setPrintView]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [deleteId, setDeleteId]       = useState(null);
  const [activateId, setActivateId]   = useState(null);

  const { products, product } = useSelector((state) => state.product);
  const { categories }        = useSelector((state) => state.category);
  const { userType }          = useSelector((state) => state.auth);
  const isAdmin               = userType === "admin";

  const categoryAttributes = useMemo(() => {
    if (!productForm.category) return [];
    const found = categories.find(c => c._id === productForm.category);
    return found?.attributes || [];
  }, [productForm.category, categories]);

  // ── Box calculation for totalQuantity in product form ─────────
  const productFormCategory = useMemo(() =>
    categories.find(c => c._id === productForm.category),
  [categories, productForm.category]);

const productFormUnit = productFormCategory?.measurementUnit || "pcs";

const productQtyBuyOptions = useMemo(() => {
  if (!productForm.totalQuantity) return null;
  const qty = Number(productForm.totalQuantity);
  if (!qty || qty <= 0) return null;
  // Need sizePerItem and itemsPerBox from the form
  const mockProduct = {
    sizePerItem: productForm.sizePerItem
      ? Number(productForm.sizePerItem) : null,
    itemsPerBox: productForm.itemsPerBox
      ? Number(productForm.itemsPerBox) : null,
  };
  if (!mockProduct.itemsPerBox && !mockProduct.sizePerItem) return null;
  return calcBuyOptions(qty, mockProduct, productFormUnit);
}, [
  productForm.totalQuantity,
  productForm.sizePerItem,
  productForm.itemsPerBox,
  productFormUnit,
]);

const productQtyBoxInfo = useMemo(() => {
  if (!productForm.totalQuantity) return null;
  const mockProduct = {
    sizePerItem: productForm.sizePerItem
      ? Number(productForm.sizePerItem) : null,
    itemsPerBox: productForm.itemsPerBox
      ? Number(productForm.itemsPerBox) : null,
    category: { measurementUnit: productFormUnit },
  };
  if (!mockProduct.itemsPerBox) return null;
  return getBoxInfo(Number(productForm.totalQuantity), mockProduct);
}, [
  productForm.totalQuantity,
  productForm.sizePerItem,
  productForm.itemsPerBox,
  productFormUnit,
]);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getProducts());
    dispatch(getallCategories());
  }, [dispatch]);

  useEffect(() => {
    if (product && editId) {
      setProductForm({
        name:        product.name            ?? "",
        sku:         product.sku             ?? "",
        category:    product.category?._id   ?? "",
        description: product.description     ?? "",
        sellingPrice: product.sellingPrice    ?? "",
        minSellingPrice: product.sellingPriceRange.minSellingPrice ?? "",
        maxSellingPrice: product.sellingPriceRange.maxSellingPrice ?? "",
        totalQuantity: product.totalQuantity != null ? product.totalQuantity : "",
        sizePerItem: product.sizePerItem != null ? product.sizePerItem : "",
        itemsPerBox: product.itemsPerBox != null ? product.itemsPerBox : "",
        sizePerBox:  product.sizePerBox  != null ? product.sizePerBox  : "",
        itemWidth:   "",
        itemLength:  "",
        attributes:  product.attributes?.map(a => ({
          attribute: a.attribute?._id || a.attribute,
          value:     a.value || ""
        })) || [],
      });
    }
  }, [product, editId]);



  useEffect(() => {
    if (!productForm.category) return;
    setProductForm(prev => ({
      ...prev,
      attributes: categoryAttributes.map(attr => {
        const existing = prev.attributes.find(
          a => a.attribute === attr._id || a.attribute?._id === attr._id
        );
        return { attribute: attr._id, value: existing?.value || "" };
      })
    }));
  }, [productForm.category, categoryAttributes.length]);

  // ─── Modal openers ───────────────────────────────────────────
  const openAddModal = () => {
    setProductForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    dispatch(getSingleProduct(id));
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  const openActivateModal = (id) => {
    setActivateId(id);
    document.activeElement?.blur();
    new Modal(activateModalRef.current).show();
  };

  const handleBoxFieldChange = (e) => {
  const { name, value } = e.target;
  const num = value === "" ? "" : Number(value);

  setProductForm(prev => {
    const updated = { ...prev, [name]: num };

    // Only one direction: sizePerItem × itemsPerBox = sizePerBox
    const si = name === "sizePerItem" ? num : Number(prev.sizePerItem) || 0;
    const ib = name === "itemsPerBox" ? num : Number(prev.itemsPerBox) || 0;

    if (si > 0 && ib > 0) {
      updated.sizePerBox = +(si * ib).toFixed(4);
    } else {
      updated.sizePerBox = "";
    }

    return updated;
  });
};

  // Width × Length → auto-fill sizePerItem
  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => {
      const updated = { ...prev, [name]: value };
      const w = name === "itemWidth"  ? Number(value) : Number(prev.itemWidth);
      const l = name === "itemLength" ? Number(value) : Number(prev.itemLength);
      if (w > 0 && l > 0) {
        const size = +(w * l).toFixed(4);
        updated.sizePerItem = size;
        // cascade
        if (prev.itemsPerBox) updated.sizePerBox = +(size * prev.itemsPerBox).toFixed(4);
      }
      return updated;
    });
  };

  // ─── Input handlers ──────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => ({ ...prev, [name]: value }));
  };



  const handleAttributeChange = (attributeId, value) => {
  setProductForm(prev => {
    const updatedAttributes = prev.attributes.map(a =>
      a.attribute === attributeId ? { ...a, value } : a
    );

    // ── Check if the changed attribute is "Size" ──────────────
    const changedAttr = categoryAttributes.find(
      a => a._id === attributeId
    );
    const isSize = changedAttr?.name?.toLowerCase() === "size";

    if (isSize && value) {
      const selectedCategory = categories.find(c => c._id === prev.category);
      const unit = selectedCategory?.measurementUnit || "pcs";
      const preset = getPresetFields(value, unit);

      if (preset) {
        // ✅ Auto-fill box fields from preset
        return {
          ...prev,
          attributes:  updatedAttributes,
          sizePerItem: preset.sizePerItem ?? "",
          itemsPerBox: preset.itemsPerBox ?? "",
          sizePerBox:  preset.sizePerBox  ?? "",
          itemWidth:   preset._width  ? String(preset._width)  : prev.itemWidth,
          itemLength:  preset._length ? String(preset._length) : prev.itemLength,
        };
      }
    }

    return { ...prev, attributes: updatedAttributes };
  });
};

  // ─── Submit directly from modal ───────────────────────────────
  // ✅ No staging list — submit immediately, keep modal open
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const { name, sku, category, } = productForm;
    if (!name || !category) {
      return toast.error("Please fill in all required fields");
    }
    setIsLoading(true);
    await dispatch(createProduct({
      name,
      sku, // Let backend generate if empty
      category,
      description: productForm.description,
      attributes:  productForm.attributes,
      sizePerItem: productForm.sizePerItem !== "" ? Number(productForm.sizePerItem) : null,
      itemsPerBox: productForm.itemsPerBox !== "" ? Number(productForm.itemsPerBox) : null,
      sizePerBox:  productForm.sizePerBox  !== "" ? Number(productForm.sizePerBox)  : null,
    }));
    setIsLoading(false);
    // ✅ Reset form but keep modal open so user can add more
    setProductForm(initialState);
    toast.success("Product added — fill in another or close the modal.");
    dispatch(getProducts());
  };

  // ─── Edit submit ──────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await dispatch(updateProduct({
      id: editId,
      data: {
        name:        productForm.name,
        sku:         productForm.sku,
        category:    productForm.category,
        sellingPrice: productForm.sellingPrice,
        minSellingPrice: productForm.minSellingPrice,
        maxSellingPrice: productForm.maxSellingPrice,
        totalQuantity: productForm.totalQuantity !== "" ? Number(productForm.totalQuantity) : null,
        description: productForm.description,
        attributes:  productForm.attributes,
        sizePerItem: productForm.sizePerItem !== "" ? Number(productForm.sizePerItem) : null,
        itemsPerBox: productForm.itemsPerBox !== "" ? Number(productForm.itemsPerBox) : null,
        sizePerBox:  productForm.sizePerBox  !== "" ? Number(productForm.sizePerBox)  : null,
      }
    }));
    dispatch(getProducts());
    Modal.getInstance(editModalRef.current).hide();
  };

  // ─── Delete ───────────────────────────────────────────────────
  const handleDelete = () => {
    dispatch(deleteProduct(deleteId));
    Modal.getInstance(deleteModalRef.current).hide();
  };

  const handleActivate = () => {
    dispatch(activateProduct(activateId));
    Modal.getInstance(activateModalRef.current).hide();
  };

  const handlePrint = () => setPrintView(true);
  const handleBeforeGetContent = async () => await setPrintView(true);

  // ─── Shared form fields ───────────────────────────────────────
  const renderFormFields = (isedit=false) => {

    // ── Inside renderFormFields — replace the old box section ──────
      // Helper — detect if selected category has "Boxed" attribute
      // and what the measurement unit is

      // ── Box & size section — only shown when category has Boxed attribute

    const selectedCategory = categories.find(c => c._id === productForm.category);
    const measurementUnit  = selectedCategory?.measurementUnit || "pcs";
    const isBoxedCategory  = selectedCategory?.attributes?.some(
      a => (a.name || a.attribute?.name)?.toLowerCase() === "boxed"
    );
    return (
      <div className="row g-3">

        <div className="col-12">
          <label className="fs-4">Category</label>

          <Select
            className="react-select-container fs-4"
            classNamePrefix="react-select fs-4"
            
            options={categories.map(cat => ({
              value: cat._id,
              label: cat.name
            }))}

            value={
              categories
                .map(cat => ({ value: cat._id, label: cat.name }))
                .find(opt => opt.value === productForm.category) || null
            }

            onChange={(selected) =>
              setProductForm({
                ...productForm,
                category: selected ? selected.value : ""
              })
            }

            placeholder="Select a category..."
            isClearable
          />
        </div>

        <div className="col-12">
          <label className="fs-4">Product Name</label>
          <input
            className="form-control form-control-lg border-2"
            placeholder="e.g. China Floor Smooth 60x60"
            type="text"
            name="name"
            value={productForm.name}
            onChange={handleInputChange}
          />
        </div>

        <div className="col-6">
          <label className="fs-4">Code</label>
          <input
            className="form-control form-control-lg border-2"
            placeholder="e.g. CER-CHN-60-SM-001"
            type="text"
            name="sku"
            value={productForm.sku}
            onChange={handleInputChange}
          />
        </div>
        {/* ── Attributes ── */}
{categoryAttributes.length > 0 && (
  <div className="col-12">
    <label className="fs-4 d-block mb-2">Attributes</label>
    <div className="bg-white border rounded-3 px-3 py-3">
      <div className="row g-3">
        {categoryAttributes.map((attr) => {
          const attrEntry = productForm.attributes.find(
            a => a.attribute === attr._id || a.attribute?._id === attr._id
          );
          const isSize = attr.name?.toLowerCase() === "size";
          return (
            <div key={attr._id} className="col-6">
              <label className="form-label fw-medium mb-1 fs-4">
                {attr.name}
                {attr.required && <span className="text-danger ms-1">*</span>}
                {/* Show "preset applied" badge when Size has a matching preset */}
                {isSize && attrEntry?.value &&
                  hasPreset(attrEntry.value, selectedCategory?.measurementUnit) && (
                  <span className="badge bg-success bg-opacity-25 text-success ms-2 fs-5">
                    preset applied
                  </span>
                )}
              </label>
              {attr.options?.length > 0 ? (
                <select
                  className="form-select border-2 fs-4"
                  value={attrEntry?.value || ""}
                  required={attr.required}
                  onChange={(e) => handleAttributeChange(attr._id, e.target.value)}
                >
                  <option value="">Select {attr.name}...</option>
                  {attr.options.map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="form-control border-2 fs-4"
                  type={attr.type === 'number' ? 'number' : 'text'}
                  required={attr.required}
                  placeholder={`Enter ${attr.name}`}
                  value={attrEntry?.value || ""}
                  onChange={(e) => handleAttributeChange(attr._id, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
)}

{/* ── Box & Area fields — after attributes, only for boxed categories ── */}
{isBoxedCategory && (
  <div className="col-12">
    <div className="bg-white border rounded-3 px-3 py-3">
      <p className="fw-semibold fs-4 mb-3 border-bottom pb-2">
        Box & {measurementUnit === "m2"
          ? "Area" : measurementUnit === "linear_meter"
          ? "Length" : "Quantity"} Info
        <span className="text-muted ms-2 fs-5 fw-normal">
          — auto-filled from Size preset, editable
        </span>
      </p>
      <div className="row g-3">

        {/* Width × Length toggle — m² only, for manual entry */}
        {measurementUnit === "m2" && (
          <div className="col-12">
            <div className="form-check form-switch mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="dimensionToggle"
                checked={!!productForm._showDimensions}
                onChange={() => setProductForm(prev => ({
                  ...prev, _showDimensions: !prev._showDimensions
                }))}
              />
              <label className="form-check-label fs-5" htmlFor="dimensionToggle">
                Enter Width × Length manually
              </label>
            </div>
            {productForm._showDimensions && (
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="fs-4">Width (m)</label>
                  <input
                    className="form-control form-control-lg border-2"
                    type="number" min="0" step="0.01"
                    placeholder="e.g. 0.60"
                    name="itemWidth"
                    value={productForm.itemWidth}
                    onChange={handleDimensionChange}
                  />
                </div>
                <div className="col-6">
                  <label className="fs-4">Length (m)</label>
                  <input
                    className="form-control form-control-lg border-2"
                    type="number" min="0" step="0.01"
                    placeholder="e.g. 0.60"
                    name="itemLength"
                    value={productForm.itemLength}
                    onChange={handleDimensionChange}
                  />
                </div>
                {productForm.itemWidth && productForm.itemLength && (
                  <div className="col-12">
                    <p className="text-success mb-0 fs-5">
                      ✓ {productForm.itemWidth} × {productForm.itemLength} ={" "}
                      {(Number(productForm.itemWidth) *
                        Number(productForm.itemLength)).toFixed(4)} m² per piece
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* sizePerItem */}
        {measurementUnit !== "pcs" && (
          <div className="col-4">
            <label className="fs-4">
              {measurementUnit === "m2" ? "m² per Piece" : "Meters per Piece"}
            </label>
            <input
              className="form-control form-control-lg border-2"
              type="number" min="0" step="0.0001"
              placeholder={measurementUnit === "m2" ? "e.g. 0.36" : "e.g. 0.6"}
              name="sizePerItem"
              value={productForm.sizePerItem}
              onChange={handleBoxFieldChange}
            />
            <div className="form-text fs-5">
              {measurementUnit === "m2" ? "Area of one piece" : "Length of one piece"}
            </div>
          </div>
        )}

        {/* itemsPerBox */}
        <div className={measurementUnit === "pcs" ? "col-6" : "col-4"}>
          <label className="fs-4">Items per Box</label>
          <input
            className="form-control form-control-lg border-2"
            type="number" min="0" step="1"
            placeholder="e.g. 4"
            name="itemsPerBox"
            value={productForm.itemsPerBox}
            onChange={handleBoxFieldChange}
          />
          <div className="form-text fs-5">Pieces per carton</div>
        </div>

        {/* sizePerBox — always auto-calculated, read-only */}
        {measurementUnit !== "pcs" && (
          <div className="col-4">
            <label className="fs-4">
              {measurementUnit === "m2" ? "m² per Box" : "Meters per Box"}
              <span className="badge bg-success bg-opacity-25 text-success ms-2 fs-5">
                auto
              </span>
            </label>
            <input
              className="form-control form-control-lg border-2 bg-white"
              type="number"
              readOnly
              tabIndex={-1}
              value={productForm.sizePerBox || ""}
              style={{ color: "#198754", fontWeight: 600 }}
            />
            <div className="form-text fs-5">
              = {productForm.sizePerItem || "?"} × {productForm.itemsPerBox || "?"}
            </div>
          </div>
        )}

      </div>
    </div>
  </div>
)}

      {isedit && (
        <>
          <div className="col-6">
            <label className="fs-4">Selling Price</label>
            <input
              className="form-control form-control-lg border-2"
              type="number" min="0"
              placeholder="enter minimum selling price"
              name="sellingPrice"
              value={productForm.sellingPrice}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-6">
            <label className="fs-4">Minimum Selling Price</label>
            <input
              className="form-control form-control-lg border-2"
              type="number" min="0"
              name="minSellingPrice"
              value={productForm.minSellingPrice}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-6">
            <label className="fs-4">Maximum Selling Price</label>
            <input
              className="form-control form-control-lg border-2"
              type="number" min="0"
              name="maxSellingPrice"
              value={productForm.maxSellingPrice}
              onChange={handleInputChange}
            />
          </div>

          {/* ── Total Quantity with box calculator ── */}
          <div className="col-12">
            <label className="fs-4">
              Total Quantity
              <span className="text-muted ms-1 fs-5">
                ({productFormUnit === "m2" ? "m²"
                  : productFormUnit === "linear_meter" ? "m" : "pcs"})
              </span>
            </label>
            <input
              className="form-control form-control-lg border-2"
              type="number" min="0" step="0.001"
              placeholder="0"
              name="totalQuantity"
              value={productForm.totalQuantity}
              onChange={handleInputChange}
            />

            {/* ── Buy options — same pattern as batch and sales ── */}
            {productQtyBuyOptions && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                <button type="button"
                  className={`btn btn-sm rounded-3 fs-5 ${
                    Number(productForm.totalQuantity) ===
                    productQtyBuyOptions.up.quantityValue
                      ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setProductForm(prev => ({
                    ...prev,
                    totalQuantity: productQtyBuyOptions.up.quantityValue
                  }))}>
                  <span className="badge bg-success me-1 fs-6">Recommended</span>
                  ▲ {productQtyBuyOptions.up.sizeLabel}{" "}
                  — {productQtyBuyOptions.up.pieces} pcs
                  {productQtyBuyOptions.up.boxLabel && (
                    <span className="ms-1 opacity-75">
                      ({productQtyBuyOptions.up.boxLabel})
                    </span>
                  )}
                </button>

                {productQtyBuyOptions.down && (
                  <button type="button"
                    className={`btn btn-sm rounded-3 fs-5 ${
                      Number(productForm.totalQuantity) ===
                      productQtyBuyOptions.down.quantityValue
                        ? "btn-warning text-dark" : "btn-outline-warning text-dark"
                    }`}
                    onClick={() => setProductForm(prev => ({
                      ...prev,
                      totalQuantity: productQtyBuyOptions.down.quantityValue
                    }))}>
                    ▼ {productQtyBuyOptions.down.sizeLabel}{" "}
                    — {productQtyBuyOptions.down.pieces} pcs
                    {productQtyBuyOptions.down.boxLabel && (
                      <span className="ms-1 opacity-75">
                        ({productQtyBuyOptions.down.boxLabel})
                      </span>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Box info for entered quantity */}
            {productQtyBoxInfo && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                <span className="badge bg-primary bg-opacity-25 text-primary
                                rounded-pill px-3 py-1 fs-5">
                  {productQtyBoxInfo.boxLabel}
                </span>
                <span className="badge bg-info bg-opacity-25 text-info
                                rounded-pill px-3 py-1 fs-5">
                  {productQtyBoxInfo.sizeLabel}
                </span>
                {productFormUnit !== "pcs" && productQtyBoxInfo.totalPcs != null && (
                  <span className="badge bg-secondary bg-opacity-25 text-secondary
                                  rounded-pill px-3 py-1 fs-5">
                    {productQtyBoxInfo.totalPcs} pcs total
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      )}

        

        <div className="col-12">
          <label className="fs-4">Description</label>
          <textarea
            name="description"
            value={productForm.description}
            onChange={handleInputChange}
            className="form-control border-2"
            rows={2}
            placeholder="Optional product description..."
          />
        </div>

      </div>
  )};

  // ─── Table columns ────────────────────────────────────────────
  // ✅ Attributes rendered as string for filter compatibility
  // ✅ Null values show "—"

  const attributeNames = useMemo(() => {
  const set = new Set();

  products.forEach(p => {
    p.attributes?.forEach(a => {
      if (a.attribute?.name) {
        set.add(a.attribute.name);
      }
    });
  });

  return [...set];
}, [products]);

const normalizedData = useMemo(() => {
  return products.map(product => {
    const attrMap = {};

    product.attributes?.forEach(a => {
      if (a.attribute?.name) {
        attrMap[a.attribute.name] = a.value;
      }
    });

    return {
      ...product,
      ...attrMap // 👈 inject dynamic keys
    };
  });
}, [products]);

console.log("Normalized Data:", normalizedData);

const attributeColumns = useMemo(() => {
  return attributeNames.map(attrName => ({
    Header: attrName,
    accessor: attrName,

    Filter: SelectColumnFilter,
    filter: 'multiSelectFilter',

    Cell: ({ value }) => (
      value ? (
        <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-2 py-1 fs-6">
          {value}
        </span>
      ) : <span className="text-muted">—</span>
    )
  }));
}, [attributeNames, products]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'No',
        accessor: 'no',
        Cell: ({ row }) => row.index + 1
      },
      {
        Header: 'Name',
        accessor: 'name',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => value || "—"
      },
      {
        Header: 'Code',
        accessor: 'sku',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => value || "—"
      },
      {
        Header: 'Box Info',
        id: 'boxInfo',
        accessor: row => row,
        Cell: ({ value: p }) => {

          if (!p.itemsPerBox && !p.sizePerItem) return <span className="text-muted">—</span>;

          const rem = p.totalQuantity || 0;
          const info = getBoxInfo(rem, p);

          return (
            <div className="d-flex flex-column gap-1" style={{ minWidth: 130 }}>
              {p.sizePerItem && (
                <span className="badge bg-info bg-opacity-25 text-info rounded-pill px-2 py-1 fs-5">
                  {p.sizePerItem} {(info.unitLabel == "pcs") ? "m²" : info.unitLabel}
                </span>
              )}
              {p.itemsPerBox && (
                <span className="badge bg-warning bg-opacity-25 text-warning rounded-pill px-2 py-1 fs-5">
                  {p.itemsPerBox} pcs/box
                </span>
              )}
              {p.itemsPerBox && p.totalQuantity > 0 && (
                <span className="badge bg-success bg-opacity-25 text-success rounded-pill px-2 py-1 fs-5">
                  {info.boxLabel}
                </span>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Category',
        accessor: 'category.name',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => value || "—"
      },
      {
        Header: 'Stock',
        accessor: 'totalQuantity',
        Cell: ({ value }) => (value ?? 0).toFixed(2)
      },
      {
        Header: 'Min Price',
        accessor: 'sellingPriceRange.minSellingPrice',
        Cell: ({ value }) => value != null ? value : "—"
      },
      {
        Header: 'Max Price',
        accessor: 'sellingPriceRange.maxSellingPrice',
        Cell: ({ value }) => value != null ? value : "—"
      },
      {
      Header: 'Attributes',
      columns: attributeColumns
    },
      {
        Header: 'Date',
        accessor: 'createdAt',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"

      },
    ];

    if (!printView && isAdmin) {
      baseColumns.push({
        Header: 'Action',
        Cell: ({ row }) => (
          <>
            <span className="text-warning" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openEditModal(row.original._id)}>
              <MdEditSquare size={24} />
            </span>

            {row.original.isActive ? (
              <span className="text-danger" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openDeleteModal(row.original._id)}>
              <MdDeleteForever size={24} />
              </span>
              
            ) : (
              <span className="text-info" style={{ padding: '5px', cursor: 'pointer' }}
                onClick={() => openActivateModal(row.original._id)}>
                <VscLayersActive size={24} />
              </span>
            )}
            
          </>
        )
      });
    }

    return baseColumns;
  }, [printView, isAdmin, attributeNames, products]);

  const data        = useMemo(() => normalizedData, [normalizedData]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, 
    data, 
    initialState: { 
      hiddenColumns: ['_id', 'reference', 'supplier', 'Size', 'Boxed', 'one box quantity', 'Isprinting'],
      sortBy: [{ id: 'name', desc: false }], 
    },
    filterTypes,
    defaultColumn: { Filter: () => null }
  }, useFilters, useGlobalFilter, useSortBy, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow, allColumns, getToggleHideAllColumnsProps,
    getSortByToggleProps, 
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div className="Orders">
      {/* ── ADD MODAL ─────────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={addModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3 ">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <AiOutlinePlus color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Add Product</h3>
                  <small className="text-muted fs-5">
                    Fill in and submit — modal stays open to add more products.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            {/* ✅ form submit = direct dispatch, no staging */}
            <form onSubmit={handleAddSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields()}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button
                  type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">
                  Close
                </button>
                {isLoading ? (
                  <ButtonLoading
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2"
                    disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2">
                    Save Product
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ────────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Product</h3>
                  <small className="text-muted fs-5">
                    Update the product details and save changes.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form onSubmit={handleEditSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(true)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal">Cancel</button>
                <button type="submit"
                  className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                  Update Product
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ──────────────────────────────────────── */}
      <div className="modal fade" ref={deleteModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">

            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}>
                  <MdDeleteForever color="red" size={34} />
                </div>
                <div>
                  <h5 className="modal-title fw-semibold mb-0">Delete Product</h5>
                  <small className="text-muted">This action cannot be undone.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0">
                  Are you sure you want to delete this product? All associated
                  data will be permanently removed.
                </p>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal">Cancel</button>
              <button className="btn btn-danger flex-fill rounded-3 fw-medium py-2"
                onClick={handleDelete}>
                <MdDeleteForever size={18} className="me-1" />Delete
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Activate Modal */}
        <div className="modal fade" ref={activateModalRef} tabIndex="-1" data-bs-focus="false">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}
                >
                  <VscLayersActive color="green" size={40} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Activate Product</h3>
                  <small className="text-muted fs-5">
                    Activating this product will make it available for use again.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0 h-3">
                  Are you sure you want to activate this product? It will be made available for use again.
                </p>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button
                className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                className="btn btn-success flex-fill rounded-3 fw-medium py-2"
                onClick={handleActivate}
              >
                Activate
              </button>
            </div>
          </div>
        </div>
      </div>

      <div>

        {/* ── PRODUCTS TABLE ────────────────────────────────── */}
          <>
            <div className="d-flex justify-content-between align-items-center m-4 px-2 py-2 rounded-4 border"
              style={{ backgroundColor: "#faf8f5" }}>
              <h4 className="mb-0 fs-2">Products</h4>
          <div className="d-flex gap-2">
              <ReactToPrint
                trigger={() => (
                  <button className="btn btn-primary rounded-3" onClick={handlePrint}>
                    Print Table
                  </button>
                )}
                content={() => componentRef.current}
                onAfterPrint={() => setPrintView(false)}
                onBeforeGetContent={handleBeforeGetContent}
              />
              <button className="btn btn-primary rounded-3" onClick={openAddModal}>
              <AiOutlinePlus className="me-1" /> Add Product
              </button>
              </div>
            </div>

            <div className="px-4 mb-4">
              <div className="d-flex align-items-center justify-content-between gap-3 px-4 rounded-4 border"
                style={{ backgroundColor: "#faf8f5" }}>
                <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
                <ColumnToggle allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps} />
              </div>
            </div>

            <div className="px-4">
              <div style={{ margin: printView ? "30px" : null }} ref={componentRef}>
                {printView && (
                  <>
                    <div style={{ padding: "1.5rem" }}><h2>Products</h2></div>
                    <div style={{ marginBottom: '1rem', textAlign: "right", fontSize: "1.5rem" }}>
                      Date: {new Date().toLocaleDateString()}
                    </div>
                  </>
                )}
                <div className="table-responsive rounded-4 border mt-5" style={{ backgroundColor: "#ffffff" }}>
                  <table className="table table-hover table-striped caption-top fs-4"
                    {...getTableProps()}
                    style={{ minWidth: "100%", fontSize: "12px", margin: 0 }}>
                    <thead>
                      {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map(column => (
                            <th className="px-4"
                              style={{ backgroundColor: "#faf8f5" }}
                              {...column.getHeaderProps(column.getSortByToggleProps())}>
                              {column.render('Header')}
                              {!printView && column.canFilter && column.render('Filter')}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map(row => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map(cell => (
                              <td className="px-4"
                                style={{ backgroundColor: "#ffffff" }}
                                {...cell.getCellProps()}>
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <ul className="pagination justify-content-center pagination-lg fs-4">
                <span style={{ padding: "5px"}}>
                  page{' '}
                  <strong>
                    <em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}
                  </strong>
                </span>
                <select className="form-select form-select-lg fs-4" style={{ maxWidth: '110px' }}
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}>
                  {[10, 15, 20, 30, 40, 50, data.length].map((size, i) => (
                    <option key={i} value={size}>Show {size}</option>
                  ))}
                </select>
                <li>
                  <button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>
                </li>
                <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
                </li>
                <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
                </li>
                <li>
                  <button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>
                </li>
              </ul>
            </div>
          </>
      </div>
    </div>
  );
}